import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { authenticatedGet } from 'remote/api';
import { Client, ProfileContextType } from './types';
import { useParams } from 'react-router-dom';

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const PortfolioProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [clientData, setClientData] = useState<Client | null>(null);
  const [selectedTab, setSelectedTab] = useState<string>('connect');
  const { id } = useParams();

  const fetchClientData = useCallback(async () => {
    try {
      const response = await authenticatedGet(
        `/advisor/clients/${id}/portfolio`,
      );

      const profile = response.data.client.profile;
      const data: Client = {
        id: response.data.client.id,
        uuid: response.data.client.uuid,
        kyc_status: response.data.client.kyc_status,
        is_active: response.data.client.is_active,
        email: response.data.client.email,
        phone: response.data.client.phone,
        churned_at: response.data.client.churned_at,
        profile: profile,
        subscription_plan: response.data.client.subscription_plan,
        estimated_aum: response.data.client.estimated_aum,
        user_status: response.data.client.user_status,
        last_activity: response.data.client.last_activity,
        last_activity_time: response.data.client.last_activity_time,
        wealth_check_data: response.data.client.wealth_check_data,
        invite_completed_by: response.data.client.invite_completed_by,
        portfolio: response.data.portfolio,
      };
      setClientData(data);
    } catch {
      console.error('Failed to fetch client data');
    }
  }, [id]);

  useEffect(() => {
    fetchClientData();
  }, [fetchClientData]);

  const refetchClientData = () => {
    fetchClientData();
  };

  return (
    <ProfileContext.Provider
      value={{
        clientData,
        setClientData,
        refetchClientData,
        selectedTab,
        setSelectedTab,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const usePortfolio = (): ProfileContextType => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
};
